import type { ParseKeys } from 'i18next'

import { ApiError } from '@/lib/api'

/* The RFC 9457 problem type suffix is the CONTRACT (AD-22); the message is not.
 *
 * A problem document carries `title` and `detail` written server-side in
 * English, for a developer reading a log. Rendering those to a user is how a
 * Russian interface acquires English sentences in exactly the moments things go
 * wrong — which is when a reader is least able to cope with a second language
 * (AD-27).
 *
 * So the client maps the stable suffix to its own translated message. The
 * suffix set below mirrors the server's checked-in registry in
 * platform/httpx/problem.go, and a test asserts the two agree: a suffix the
 * server can send with no entry here would fall through to a generic message
 * and lose the specific one somebody wrote.
 */

/** Every suffix the server's registry declares. */
export const PROBLEM_SUFFIXES = [
  'validation-failed',
  'auth-required',
  'forbidden',
  'password-change-required',
  'not-found',
  'conflict',
  'idempotency-conflict',
  'issuing-entity-inactive',
  'rate-missing',
  'leg-units-changed',
  'allocation-over-ceiling',
  'allocation-target-closed',
  'case-has-no-client',
  'rate-limited',
  'payload-too-large',
  'unsupported-media-type',
  'unavailable',
  'internal',
] as const

export type ProblemSuffix = (typeof PROBLEM_SUFFIXES)[number]

/** A translated title and detail for one problem. */
export interface ProblemMessage {
  titleKey: ParseKeys
  detailKey: ParseKeys
  /** Whether offering "try again" is honest. A 403 will answer 403 again. */
  retryable: boolean
}

const MESSAGES: Record<ProblemSuffix, ProblemMessage> = {
  'validation-failed': {
    titleKey: 'errors.problems.validationFailed.title',
    detailKey: 'errors.problems.validationFailed.detail',
    retryable: false,
  },
  'auth-required': {
    titleKey: 'errors.problems.authRequired.title',
    detailKey: 'errors.problems.authRequired.detail',
    retryable: false,
  },
  forbidden: {
    titleKey: 'errors.problems.forbidden.title',
    detailKey: 'errors.problems.forbidden.detail',
    retryable: false,
  },
  /* QW-5: the account still holds a credential somebody else generated, and
     every route but the session read and the change itself refuses until it
     is replaced. RequireSession redirects on it (lib/query.ts re-reads the
     session on this suffix), so this message is only what a screen shows for
     the moment before that lands. Not retryable: it answers 403 until the
     password changes. */
  'password-change-required': {
    titleKey: 'errors.problems.passwordChangeRequired.title',
    detailKey: 'errors.problems.passwordChangeRequired.detail',
    retryable: false,
  },
  'not-found': {
    titleKey: 'errors.problems.notFound.title',
    detailKey: 'errors.problems.notFound.detail',
    retryable: false,
  },
  conflict: {
    titleKey: 'errors.problems.conflict.title',
    detailKey: 'errors.problems.conflict.detail',
    retryable: false,
  },
  'idempotency-conflict': {
    titleKey: 'errors.problems.idempotencyConflict.title',
    detailKey: 'errors.problems.idempotencyConflict.detail',
    retryable: false,
  },
  /* A 409 of its own rather than a `conflict` detail, so the reader is told
     what to fix (P0-2): the record would be registered and numbered under an
     issuing entity that has been switched off. Today only a quote's conversion
     answers it. Not retryable — the same request answers the same way until
     somebody reactivates the entity in Settings. */
  'issuing-entity-inactive': {
    titleKey: 'errors.problems.issuingEntityInactive.title',
    detailKey: 'errors.problems.issuingEntityInactive.detail',
    retryable: false,
  },
  /* AD-20's loud refusal: a write that needs an FX rate the cache does not
     hold (S-4.1 follow-ups). The problem carries `currency` and `rate_date`,
     and `rateMissingMessage` (lib/refusals) words it from them — today's rate
     is a matter of waiting for the hourly fill, a past date's is an
     administrator's hand entry. This entry is the sentence for a refusal that
     arrived without them, and it is not retryable because the TYPE does not
     say which of the two it is. */
  'rate-missing': {
    titleKey: 'errors.problems.rateMissing.title',
    detailKey: 'errors.problems.rateMissing.detail',
    retryable: false,
  },
  /* S-10: a leg save whose unit list was read before somebody else saved
     units on the same leg (S-18b renamed it from `leg-trucks-changed` when a
     leg's trucks became one kind of unit among several). Nothing was written.
     The leg page refetches the leg, keeps the reader's unsaved rows and asks
     for the save again — so it is not retryable as a REQUEST: re-sending the
     same body carries the same stale version and answers 409 again. */
  'leg-units-changed': {
    titleKey: 'errors.problems.legUnitsChanged.title',
    detailKey: 'errors.problems.legUnitsChanged.detail',
    retryable: false,
  },
  /* S-21: a split refused by one of AD-11's two ceilings — the payment's
     amount, or the receivable's total. The problem carries `ceiling`, the
     target's kind, id and number (or tranche place) and `remaining`; the
     client payment sheet words it from them. This entry is the sentence for a
     refusal read without them. Not retryable: the same split answers the same
     way until somebody changes the amounts. */
  'allocation-over-ceiling': {
    titleKey: 'errors.problems.allocationOverCeiling.title',
    detailKey: 'errors.problems.allocationOverCeiling.detail',
    retryable: false,
  },
  /* S-21: a split against a tranche an issued invoice has replaced. The
     problem names the tranche's place and the invoice (`replaced_by`); the
     money belongs on the invoice. Not retryable. */
  'allocation-target-closed': {
    titleKey: 'errors.problems.allocationTargetClosed.title',
    detailKey: 'errors.problems.allocationTargetClosed.detail',
    retryable: false,
  },
  /* S-21: a client payment on a case that names no client — the payment's
     party is the case's client, and there is none. Not retryable. */
  'case-has-no-client': {
    titleKey: 'errors.problems.caseHasNoClient.title',
    detailKey: 'errors.problems.caseHasNoClient.detail',
    retryable: false,
  },
  'rate-limited': {
    titleKey: 'errors.problems.rateLimited.title',
    detailKey: 'errors.problems.rateLimited.detail',
    // The only refusal that becomes allowed by waiting.
    retryable: true,
  },
  'payload-too-large': {
    titleKey: 'errors.problems.payloadTooLarge.title',
    detailKey: 'errors.problems.payloadTooLarge.detail',
    retryable: false,
  },
  'unsupported-media-type': {
    titleKey: 'errors.problems.unsupportedMediaType.title',
    detailKey: 'errors.problems.unsupportedMediaType.detail',
    retryable: false,
  },
  unavailable: {
    titleKey: 'errors.problems.unavailable.title',
    detailKey: 'errors.problems.unavailable.detail',
    // Not retryable: the capability is absent from this installation, so the
    // same request answers the same way until somebody configures the service.
    // Offering "try again" would be a button that cannot work.
    retryable: false,
  },
  internal: {
    titleKey: 'errors.problems.internal.title',
    detailKey: 'errors.problems.internal.detail',
    retryable: true,
  },
}

/** No connection, DNS failure, a proxy that never answered. */
const NETWORK: ProblemMessage = {
  titleKey: 'errors.network.title',
  detailKey: 'errors.network.detail',
  retryable: true,
}

/** A response that is not a problem document — a gateway's own error page, or a
 *  status the registry does not cover. */
const UNKNOWN: ProblemMessage = {
  titleKey: 'errors.problems.unknown.title',
  detailKey: 'errors.problems.unknown.detail',
  retryable: true,
}

function isKnownSuffix(value: string): value is ProblemSuffix {
  return (PROBLEM_SUFFIXES as readonly string[]).includes(value)
}

/**
 * Maps any thrown value to a translated message.
 *
 * Takes `unknown` rather than `ApiError` because that is what a catch block and
 * a TanStack Query `error` actually hold: a caller forced to narrow first would
 * narrow inconsistently, and the network case — which is not an ApiError at all
 * — is the one that matters most on a phone in the field.
 */
export function describeProblem(error: unknown): ProblemMessage {
  if (!(error instanceof ApiError)) {
    return NETWORK
  }

  if (isKnownSuffix(error.code)) {
    return MESSAGES[error.code]
  }

  // A 5xx that arrived as a gateway's HTML error page rather than a problem
  // document still deserves the server-fault message, not "unknown".
  if (error.status >= 500) {
    return MESSAGES.internal
  }

  return UNKNOWN
}

/**
 * Field-level validation failures, keyed by field path.
 *
 * Returns an empty object for anything that is not a validation problem, so a
 * form can spread it unconditionally rather than branching.
 */
export function fieldErrors(error: unknown): Record<string, string[]> {
  if (!(error instanceof ApiError)) return {}
  return error.problem.errors ?? {}
}

/**
 * The translation key for one field-level error code.
 *
 * The server sends a stable token — `required`, `invalid`, `too-long` — for the
 * same reason it sends a suffix: the message belongs in the language the reader
 * is using. An unrecognised code falls back to a generic message rather than
 * rendering the raw token, which would read as a bug to a user.
 */
export function fieldErrorKey(code: string): ParseKeys {
  switch (code) {
    case 'required':
      return 'common.form.required'
    case 'invalid':
      return 'common.form.invalid'
    case 'too-long':
      return 'common.form.tooLong'
    case 'malformed':
      return 'common.form.malformed'
    /* The money writes' own codes (finance/expense_folded.go, invoice.go,
       debitnote.go). Folded into `invalid` they told the reader a 0.00 was
       wrong without saying how (QW-9, E-1). */
    case 'not-positive':
      return 'common.form.notPositive'
    case 'negative':
      return 'common.form.negative'
    case 'too-precise':
      return 'common.form.tooPrecise'
    /* The folded expense PUT on an unplanned line that was sent a plan
       (finance/expense_folded.go, S-4.1): such a line stays unplanned. */
    case 'unplanned-line':
      return 'common.form.unplannedLine'
    /* identity/roles_service.go (story B3): an edit narrowing the superuser's
       row scope. RoleFormSheet hides that section for the superuser, so only a
       stale tab or another client sends one. */
    case 'superuser-scopes-fixed':
      return 'common.form.superuserScopesFixed'
    /* identity (S-7, O7): a role, or an own grant, wider than the caller's
       own authority — on `role` or `capabilities` of an account write. */
    case 'exceeds-authority':
      return 'common.form.exceedsAuthority'
    /* identity (S-7, O8): `admin.roles.manage` is the superuser's alone, so
       no role and no account may be given it — on `capabilities`. */
    case 'reserved':
      return 'common.form.reserved'
    /* identity/roles_handlers.go `fail` (S-13): a role key another role
       already holds — on `key`. Folded into `invalid` it read as "Validation
       failed" with nothing to change. */
    case 'taken':
      return 'common.form.taken'
    /* identity/roles.go (S-13): a capability or a scope surface the server's
       registry does not hold — on `capabilities` / `scopes`. The editor offers
       only what the server served, so only a tab older than the server sends
       one; reloading fetches the current catalogue. */
    case 'unknown-capability':
      return 'common.form.unknownCapability'
    case 'unknown-surface':
      return 'common.form.unknownSurface'
    /* finance/carrier_payment.go (S-18a): one payment over several items.
       `exclusive` — an item naming both a line and a debt, or an unplanned
       debt naming both a truck and the whole leg; `duplicate` — the same
       target twice (on `items.<i>`); `too-many` — over 100 items;
       `sum-mismatch` — the payment amount is not the sum of the item amounts;
       `carrier-mismatch` — several items that are not all one directory
       carrier's (the last three on `items`). */
    case 'exclusive':
      return 'common.form.exclusive'
    case 'duplicate':
      return 'common.form.duplicate'
    case 'too-many':
      return 'common.form.tooMany'
    case 'sum-mismatch':
      return 'common.form.sumMismatch'
    case 'carrier-mismatch':
      return 'common.form.carrierMismatch'
    /* finance/carrier_payment_service.go (S-22d): a debt the carrier payment
       would book to a carrier typed by name only — on `items.<i>.debt`. The
       sheet does not let such a row be ticked, so only a stale tab sends one;
       the fix is a directory carrier on the unit or the leg. */
    case 'carrier-not-in-directory':
      return 'common.form.carrierNotInDirectory'
    /* casefile/finance (S-18b): `retired` — a key the server no longer reads
       (`vehicles`, `containers`, `vehiclesVersion`, `vehicleId`), sent only by a
       tab opened before the deploy, so reloading is the fix; `not-for-kind` — a
       field the row's unit kind does not carry, on `units.<i>.<field>`. */
    case 'retired':
      return 'common.form.retired'
    case 'not-for-kind':
      return 'common.form.notForKind'
    /* finance/debitnote_service.go (S-22a): a debit-note create naming any
       payer but the case's own client — on `counterparty`. The sheet names
       the client read-only, so only a stale tab or a client changed under an
       open sheet sends one. */
    case 'client-only':
      return 'common.form.clientOnly'
    /* directory/service.go (S-22b): a merge of two counterparties of
       different categories — on `category`. The merge sheet offers only the
       source's own category, so only a stale list sends one; the sheet words
       it in full (`counterpartyMergeErrorMessage`), this is the field line. */
    case 'mismatched':
      return 'common.form.mismatched'
    /* finance/payment_party.go (S-22d2): `with-counterparty` — a payment
       naming both the client and a counterparty (on `clientId`). The party
       picker never sends it, so only a stale tab meets it. Its sibling
       `counterpartyId: direction` is worded by the payment sheets' own field
       maps, not here: `direction` is also an allocation's `targetKind` code,
       which means something else. */
    case 'with-counterparty':
      return 'common.form.withCounterparty'
    default:
      return 'common.form.invalid'
  }
}
